import React from 'react'
import compose from 'recompose/compose'
import { isWidthDown } from '@material-ui/core/withWidth'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import withStyles from '@material-ui/core/styles/withStyles'
import { isObject, isElement, isFunction } from 'lib/detectType'
import sortBy from 'lodash/sortBy'
import findIndex from 'lodash/findIndex'
import { josa } from 'es-hangul'

import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import IconButton from '@material-ui/core/IconButton'
import Icon from '@material-ui/core/Icon'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import TablePagination from '@material-ui/core/TablePagination'
import Divider from '@material-ui/core/Divider'
import CircularProgress from '@material-ui/core/CircularProgress'
import Checkbox from '@material-ui/core/Checkbox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'

import withCompWidth from 'commonHoc/withCompWidth'

import TableTagList from 'components/organisms/TableTagList'

const styles = theme => ({
  TableListWrap: {
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden'
  },
  TableList: {},
  list: {
    padding: 0
  },
  headerList: {
    backgroundColor: '#f7f7f7'
  },
  headerListItemTextRoot: {
    flexBasis: 0,
    padding: 0,
    margin: 0
  },
  headerTextPrimaryRoot: {
    color: theme.palette.primary.dark,
    wordBreak: 'break-all',
    verticalAlign: 'top'
  },
  headerTextSecondaryRoot: {
    color: theme.palette.primary.dark,
    wordBreak: 'break-all'
  },
  listItemSecondaryAction: {
    paddingRight: theme.spacing(6)
  },
  listItemTextRoot: {
    flexBasis: 0,
    padding: 0
  },
  textPrimaryRoot: {
    wordBreak: 'break-all'
  },
  textSecondaryRoot: {
    wordBreak: 'break-all'
  },
  selectRoot: {
    margin: 0
  },
  actions: {
    margin: 0
  },
  loadingProgress: {
    marginBottom: theme.spacing(2)
  },
  checkboxRoot: {
    padding: 0,
    paddingRight: theme.spacing(1.5),
    paddingLeft: theme.spacing(1.5)
  },
  paginationWrap: {
    paddingLeft: theme.spacing(1) / 2,
    paddingRight: theme.spacing(1) / 2
  },
  paginationBtnWrap: {
    textAlign: 'right'
  },
  paginationRoot: {
    display: 'inline-block'
  },
  paginationLoading: {
    verticalAlign: 'middle'
  },
  checkedListItem: {
    backgroundColor: '#fcfbef',
    whiteSpace: 'pre-wrap'
  },
  uncheckedListItem: {
    whiteSpace: 'pre-wrap'
  },
  tableSortLabelIcon: {
    display: 'none'
  },
  dense: {
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: 0,
    marginBottom: 0
  },
  rowHighlight: {
    '&:hover': {
      backgroundColor: '#efefef'
    }
  },
  toolbar: {
    minHeight: 0
  }
})

const Pagination = props => {
  const {
    classes,
    proxy,
    data = [],
    options = {},
    checked = {},
    moreDataLoading = false,
    onClickUncheck,
    sideEl,
    rowsPerPageOptions
  } = props
  const {
    page = 0,
    rowsPerPage = 20,
    checkbox = {},
    sideEl: optionSideEl
  } = options
  const { onChangePage, onChangeRowsPerPage } = proxy
  const { visibility } = checkbox
  const sideElement = sideEl || optionSideEl
  return (
    <div className={classes.paginationWrap}>
      <Grid container alignItems="center" spacing={1}>
        {isElement(sideElement) && (
          <Grid item xs={visibility ? 12 : true}>
            {sideElement}
          </Grid>
        )}
        {visibility && (
          <Grid item xs>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs="auto">
                <Button
                  size="small"
                  color="primary"
                  onClick={onClickUncheck}
                  disabled={Object.keys(checked).length < 1}
                >
                  전체선택해제
                </Button>
              </Grid>
              <Grid item xs="auto">
                {Object.keys(checked).length > 0 && (
                  <Typography
                    align="center"
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    {Object.keys(checked).length}개 선택됨
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid
          item
          xs={
            !isElement(sideElement) && !visibility
              ? 12
              : visibility
              ? 'auto'
              : 12
          }
          sm={!isElement(sideElement) && !visibility ? 12 : 'auto'}
        >
          <div className={classes.paginationBtnWrap}>
            {moreDataLoading && (
              <CircularProgress
                className={classes.paginationLoading}
                size={20}
              />
            )}
            <TablePagination
              classes={{
                root: classes.paginationRoot,
                selectRoot: classes.selectRoot,
                actions: classes.actions,
                toolbar: classes.toolbar
              }}
              labelRowsPerPage=""
              labelDisplayedRows={({ from, to, count }) => {
                const totalPage = Math.ceil(count / rowsPerPage) || 1
                return `${page + 1} / ${totalPage}`
              }}
              rowsPerPageOptions={
                rowsPerPageOptions || [5, 10, 20, 50, 100, 200, 1000]
              }
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              backIconButtonProps={{
                'aria-label': 'Previous Page',
                size: 'small'
              }}
              nextIconButtonProps={{
                'aria-label': 'Next Page',
                size: 'small'
              }}
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
            />
          </div>
        </Grid>
      </Grid>
    </div>
  )
}

const TableList = props => {
  const { classes, proxy, ...others } = props
  const {
    tableKey,
    initFlag = false,
    originData = [],
    data = [],
    columns = {},
    options = {},
    checked = {},
    loading = false,
    focusRow,
    moreDataLoading,
    width,
    maxHeight,
    HelpContainer,
    sideEl
  } = others
  const {
    title = '목록',
    loadingText,
    order,
    orderBy,
    page,
    materialList = true,
    checkbox,
    rowsPerPage,
    secondaryAction,
    listItemProps = {},
    hideColumns = false,
    hidePagination = {},
    dense = false,
    rowHighlight = false,
    rowsPerPageOptions,
    extraButton
  } = options
  const {
    stableSort,
    onRequestSort,
    onClickCheckbox,
    onClickCheckAllToggle,
    onClickCheckAll,
    onClickUncheck
  } = proxy
  const stableSortData = stableSort(data, order, orderBy)
  const stableSortOriginData = sortBy(originData, oriRowData => {
    const uniqRowId = oriRowData?.uniqRowId
    const dataIndex = findIndex(
      stableSortData,
      data => data?.uniqRowId === uniqRowId
    )
    return dataIndex
  })
  const sortedData = stableSortData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )
  const sortedOriginData = stableSortOriginData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )
  const checkedLength = Object.values(checked).filter(d => d).length
  const checkedAll =
    checkedLength > 0 &&
    checkedLength ===
      sortedOriginData.filter((data, index) => {
        return isFunction(checkbox.disabled)
          ? !checkbox.disabled(data, index)
          : true
      }).length
  const sortedColumnKeys = sortBy(Object.keys(columns), key => {
    return columns[key].order
  })
  const visibleColumns = sortedColumnKeys.filter(key => {
    const { hidden = [] } = columns[key]
    return hidden.indexOf(props.width) === -1
  })
  const minWidth = materialList
    ? visibleColumns.length > 0
      ? visibleColumns
          .map(key => columns[key].minWidth || 120)
          .reduce((a, b) => a + b) + 32
      : 32
    : undefined
  return (
    <React.Fragment>
      <Grid container spacing={1}>
        {hidePagination?.top !== true && (
          <Grid item xs={12}>
            <Pagination
              checked={checked}
              data={data}
              sideEl={sideEl}
              proxy={proxy}
              options={options}
              classes={classes}
              moreDataLoading={moreDataLoading}
              rowsPerPageOptions={rowsPerPageOptions}
              onClickUncheck={onClickUncheck}
            />
          </Grid>
        )}
        {hidePagination?.top !== true && (
          <Grid item xs={12}>
            <Divider />
          </Grid>
        )}
        <Grid item xs={12}>
          <div
            className={classes.TableListWrap}
            style={{
              maxHeight: maxHeight || undefined,
              overflowY: maxHeight ? 'scroll' : undefined
            }}
          >
            <div
              className={classes.TableList}
              style={Object.assign(minWidth ? { minWidth } : {})}
            >
              {materialList === false ? (
                <TableTagList
                  {...{
                    tableKey,
                    title,
                    loading,
                    loadingText,
                    order,
                    dense,
                    orderBy,
                    columns,
                    visibleColumns,
                    data: stableSortData,
                    originData: stableSortOriginData,
                    checked,
                    checkbox,
                    focusRow,
                    hideColumns,
                    page,
                    rowsPerPage,
                    rowHighlight,
                    onRequestSort,
                    onClickCheckAllToggle,
                    onClickCheckbox,
                    listItemProps,
                    HelpContainer,
                    extraButton
                  }}
                />
              ) : (
                <List className={classes.list}>
                  {hideColumns === false && (
                    <ListItem
                      className={classNames(
                        classes.headerList,
                        secondaryAction && classes.listItemSecondaryAction,
                        { [classes.dense]: dense }
                      )}
                      divider
                    >
                      {isObject(checkbox) &&
                        checkbox.visibility &&
                        (checkbox.material === false ? (
                          <input
                            type="checkbox"
                            checked={checkedAll}
                            style={{ marginRight: 10 }}
                            onClick={e => {
                              e.stopPropagation()
                              onClickCheckAll(e, sortedOriginData)
                            }}
                            tabIndex={-1}
                            {...(checkbox.checkboxProps || {})}
                          />
                        ) : (
                          <Checkbox
                            classes={{
                              root: classes.checkboxRoot
                            }}
                            checked={checkedAll}
                            icon={
                              <CheckBoxOutlineBlankIcon
                                style={{ fontSize: checkbox.fontSize || 20 }}
                              />
                            }
                            checkedIcon={
                              <CheckBoxIcon
                                style={{
                                  fontSize: checkbox.fontSize || 20
                                }}
                              />
                            }
                            onClick={e => {
                              e.stopPropagation()
                              onClickCheckAll(e, sortedOriginData)
                            }}
                            tabIndex={-1}
                            {...(checkbox.checkboxProps || {})}
                          />
                        ))}
                      {visibleColumns.map((key, dataTextIndex) => {
                        const column = columns[key]
                        const {
                          align,
                          ratio = {},
                          primary,
                          secondary,
                          minWidth,
                          help,
                          noWrap = false,
                          columnTypographyProps = {},
                          disableSort = false
                        } = column
                        return (
                          <ListItemText
                            key={dataTextIndex}
                            classes={{
                              root: classes.headerListItemTextRoot
                            }}
                            primaryTypographyProps={{
                              classes: {
                                root: classes.headerTextPrimaryRoot
                              },
                              variant: 'body2',
                              noWrap,
                              align,
                              ...columnTypographyProps
                            }}
                            secondaryTypographyProps={{
                              classes: {
                                root: classes.headerTextSecondaryRoot
                              },
                              variant: 'body2',
                              noWrap,
                              align
                            }}
                            style={Object.assign(
                              {
                                flexGrow: ratio[props.width]
                              },
                              minWidth ? { minWidth } : {}
                            )}
                            primary={
                              disableSort ? (
                                <React.Fragment>
                                  {primary}
                                  {isObject(help) && (
                                    <HelpContainer helpKey={tableKey + key} />
                                  )}
                                </React.Fragment>
                              ) : (
                                <span>
                                  <TableSortLabel
                                    active={orderBy === key}
                                    direction={order}
                                    onClick={e => onRequestSort(key)}
                                    classes={{
                                      icon:
                                        orderBy === key
                                          ? null
                                          : classes.tableSortLabelIcon
                                    }}
                                  >
                                    {primary}
                                  </TableSortLabel>
                                  {isObject(help) && (
                                    <HelpContainer helpKey={tableKey + key} />
                                  )}
                                </span>
                              )
                            }
                            secondary={secondary || null}
                          />
                        )
                      })}
                    </ListItem>
                  )}
                  {initFlag === true &&
                    !loading &&
                    (!data || data.length === 0) && (
                      <ListItem divider>
                        <ListItemText
                          classes={{
                            root: classes.listItemTextRoot
                          }}
                          primaryTypographyProps={{
                            display: 'block',
                            align: isWidthDown('md', width) ? 'left' : 'center',
                            variant: 'body2'
                          }}
                          primary={`${josa(title, '이/가')} 없습니다.`}
                        />
                      </ListItem>
                    )}
                  {(loading || initFlag !== true) && (
                    <ListItem divider>
                      <ListItemText
                        classes={{
                          root: classes.listItemTextRoot
                        }}
                        primaryTypographyProps={{
                          align: 'center'
                        }}
                      >
                        <CircularProgress className={classes.loadingProgress} />
                        {loadingText && <div>{loadingText}</div>}
                      </ListItemText>
                    </ListItem>
                  )}
                  {!loading &&
                    sortedData.map((data, index) => {
                      return (
                        <ListItem
                          key={index}
                          divider
                          classes={{
                            root: classNames(
                              isObject(checkbox) &&
                                checkbox.visibility &&
                                !!checked[index]
                                ? classes.checkedListItem
                                : classes.uncheckedListItem,
                              { [classes.dense]: dense },
                              { [classes.rowHighlight]: rowHighlight }
                            ),
                            secondaryAction:
                              secondaryAction && classes.listItemSecondaryAction
                          }}
                          {...listItemProps}
                          onClick={e => {
                            const { onClick } = listItemProps
                            if (!onClick && typeof onClick !== 'function') {
                              return
                            }
                            return onClick(e, index, sortedOriginData[index])
                          }}
                        >
                          {isObject(checkbox) &&
                            checkbox.visibility &&
                            (checkbox.material === false ? (
                              <input
                                type="checkbox"
                                checked={!!checked[index]}
                                disabled={
                                  isFunction(checkbox.disabled)
                                    ? checkbox.disabled(
                                        sortedOriginData[index],
                                        index
                                      )
                                    : false
                                }
                                style={{ marginRight: 10 }}
                                onClick={e => {
                                  e.stopPropagation()
                                  onClickCheckbox(
                                    e,
                                    index,
                                    sortedOriginData[index]
                                  )
                                }}
                                {...(checkbox.checkboxProps || {})}
                              />
                            ) : (
                              <Checkbox
                                classes={{
                                  root: classes.checkboxRoot
                                }}
                                checked={!!checked[index]}
                                disabled={
                                  isFunction(checkbox.disabled)
                                    ? checkbox.disabled(
                                        sortedOriginData[index],
                                        index
                                      )
                                    : false
                                }
                                icon={
                                  <CheckBoxOutlineBlankIcon
                                    style={{
                                      fontSize: checkbox.fontSize || 20
                                    }}
                                  />
                                }
                                checkedIcon={
                                  <CheckBoxIcon
                                    style={{
                                      fontSize: checkbox.fontSize || 20
                                    }}
                                  />
                                }
                                tabIndex={-1}
                                onClick={e => {
                                  e.stopPropagation()
                                  onClickCheckbox(
                                    e,
                                    index,
                                    sortedOriginData[index]
                                  )
                                }}
                                {...(checkbox.checkboxProps || {})}
                              />
                            ))}
                          {visibleColumns.map((key, textIndex) => {
                            const {
                              align,
                              ratio = {},
                              noWrap = false,
                              minWidth,
                              typographyProps = {}
                            } = columns[key]
                            const value = data[key]
                            if (isElement(value)) return value
                            return (
                              <ListItemText
                                key={textIndex}
                                classes={{
                                  root: classNames(classes.listItemTextRoot, {
                                    [classes.dense]: dense
                                  })
                                }}
                                primaryTypographyProps={{
                                  classes: {
                                    root: classes.textPrimaryRoot
                                  },
                                  variant: 'body2',
                                  noWrap,
                                  align,
                                  ...typographyProps
                                }}
                                secondaryTypographyProps={{
                                  classes: {
                                    root: classes.textSecondaryRoot
                                  },
                                  variant: 'body2',
                                  noWrap,
                                  align
                                }}
                                style={Object.assign(
                                  {
                                    flexGrow: ratio[props.width]
                                  },
                                  minWidth ? { minWidth } : {}
                                )}
                                primary={
                                  isObject(value) && value.primary
                                    ? value.primary
                                    : value
                                }
                                secondary={
                                  isObject(value) && value.secondary
                                    ? value.secondary
                                    : null
                                }
                              />
                            )
                          })}
                          {secondaryAction && (
                            <ListItemSecondaryAction>
                              <IconButton
                                onClick={e => {
                                  return secondaryAction.onClick(
                                    e,
                                    index,
                                    sortedOriginData[index]
                                  )
                                }}
                              >
                                <Icon>{secondaryAction.icon}</Icon>
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                      )
                    })}
                </List>
              )}
            </div>
          </div>
        </Grid>
        {hidePagination?.bottom !== true && (
          <Grid item xs={12}>
            <Pagination
              checked={checked}
              sideEl={sideEl}
              data={data}
              proxy={proxy}
              options={options}
              classes={classes}
              moreDataLoading={moreDataLoading}
              rowsPerPageOptions={rowsPerPageOptions}
              onClickUncheck={onClickUncheck}
            />
          </Grid>
        )}
      </Grid>
    </React.Fragment>
  )
}
TableList.defaultProps = {}
TableList.propTypes = {
  classes: PropTypes.object.isRequired
}

export default compose(withStyles(styles))(
  withCompWidth(TableList, { enabled: true })
)
